<template>
  <div>
    <slot :open-file-picker="openFilePicker">
      <button class="btn btn-success" type="button" @click="openFilePicker">
        {{ $t('Choose files') }}
      </button>
    </slot>

    <FilepickerInputField
      v-if="useInputField"
      class="m-t-10"
      :input-name="inputName"
      :selected-files="inputSelectedFiles"
    />
    <ProvideApiFilebank
      v-slot="{
        loading,
        uploadFiles,
        uploading,
        fileData,
        showUploaderFilter,
        onlyMyUploads,
        setOnlyMyUploads,
      }"
      :use-global-id="useGlobalId"
      :default-tags="defaultTags"
      :exclude-ids="excludeFileIds"
      :exclude-tags="excludeTags"
      :show-all-uploads-by-default="preSelectedFileIds.length > 0"
      @initial-files-loaded="updateAutoSelectedFiles"
      @uploaded="handleRecentlyUploadedFiles"
    >
      <BaseModal
        :save-state="uploading ? 'saving' : 'fresh'"
        :is-open="isOpen"
        :header-text="$t('Your Worksome files')"
        data-test="filebank-select-and-add-button"
        :confirm-button-text="
          $t('Select and add') + ` ${selectedFileCount ? '(' + selectedFileCount + ')' : ''}`
        "
        :saving-text="$t('Uploading') + '...'"
        :no-top-margin="noTopMargin"
        :modal-id="randomModalId"
        @positive-response="emitSelection"
        @cancel="isOpen = false"
      >
        <template #topright>
          <PickerFileOs
            :multiple="true"
            :disabled="uploading"
            button-class="btn btn-success"
            data-test="filebank-upload-files"
            @files-selected="uploadFiles"
          >
            <template v-if="uploading">
              <ButtonSpinner />
              {{ $t('Uploading') }}
            </template>
            <template v-else>
              <i class="fa fa-upload" />
              {{ $t('Upload file') }}
            </template>
          </PickerFileOs>
        </template>

        <template #default>
          <WdsToggle
            v-if="showUploaderFilter"
            :checked="onlyMyUploads"
            :label="$t('Only show documents I uploaded')"
            data-test="filebank-only-my-uploads"
            @update:checked="setOnlyMyUploads"
          />
          <div style="max-height: 500px; overflow-y: auto">
            <BaseSelectableFileList
              ref="fileList"
              :exclude-ids="excludeFileIds"
              :files="fileData"
              :is-loading="loading"
              :auto-select="autoSelect"
              :use-global-id="useGlobalId"
              @selection-updated="handleFileSelection"
            />
          </div>
        </template>
      </BaseModal>
    </ProvideApiFilebank>
  </div>
</template>

<script setup>
import { WdsToggle } from '@worksome/wds-components'
import ProvideApiFilebank from '@/components/ProvideApiFilebank.vue'
import BaseModal from '@/components/BaseModal.vue'
import FilepickerInputField from '@/components/FilepickerInputField.vue'
import BaseSelectableFileList from '@/components/BaseSelectableFileList.vue'
import ButtonSpinner from '@/components/ButtonSpinner.vue'
import PickerFileOs from '@/components/PickerFileOs.vue'
</script>

<script>
/**
 * Exclude - array of file ID's to exclude
 */
import { isIterable } from '@/utils/tools'

const toFileIds = (value) => (isIterable(value) ? value : [])

export default {
  props: {
    preSelectedFiles: { type: Array, default: () => [] },
    excludeIds: { type: Array, default: () => [] },
    excludeTags: { type: Array, default: () => [] },
    noTopMargin: { type: Boolean, default: false },
    defaultTags: { type: Array[String], default: () => [] },
    useInputField: { type: Boolean, default: false },
    inputName: { type: String, default: 'uploadfiles[]' },
    useGlobalId: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedFiles: [],
      inputSelectedFiles: [],
      uploadedFiles: [],
      isOpen: false,
      autoSelect: toFileIds(this.preSelectedFiles),
    }
  },
  computed: {
    preSelectedFileIds() {
      return toFileIds(this.preSelectedFiles)
    },
    excludeFileIds() {
      return toFileIds(this.excludeIds)
    },
    idProp() {
      return this.useGlobalId ? 'globalId' : 'localId'
    },
    selectedFileCount() {
      return this.selectedFiles.length
    },
    randomModalId() {
      // Generate a random ID for the modal - otherwise we cannot have multiple instances on the same page.
      return `picker-file-filebank-${Math.random().toString(36).slice(-5)}`
    },
  },
  methods: {
    openFilePicker() {
      this.isOpen = true
    },
    handleFileSelection(selection) {
      this.selectedFiles = selection.map((file) => ({
        ...file,
        id: file[this.idProp],
      }))
    },
    emitSelection() {
      if (this.useInputField) {
        this.inputSelectedFiles = this.selectedFiles
      }
      this.$emit('files-selected', this.selectedFiles)
      this.isOpen = false
      this.$refs.fileList.uncheckAllFiles()
    },
    /**
     * this is to populate the input value with pre-selected files
     * since we only have a list of id's, we need to wait for the api to return the account's files
     * so we can collect the file objects represented by the preSelectedFiles array of id's
     * @param files
     */
    updateAutoSelectedFiles(files) {
      if (isIterable(files)) {
        this.selectedFiles = [
          ...this.selectedFiles,
          ...files.filter((file) => this.preSelectedFileIds.includes(file[this.idProp])),
        ]
      }
      if (this.useInputField) {
        this.inputSelectedFiles = this.selectedFiles
      }
    },
    handleRecentlyUploadedFiles(files) {
      this.autoSelect = files.map((file) => file[this.idProp])
    },
  },
}
</script>
